import { useState, useCallback, useRef } from "react";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";

/**
 * useFlow — data-fetching hook for Feature Flow queries.
 * Mirrors the same pattern as useInsights. Aborts the previous request when a new one starts
 * (e.g. rapid breakdown toggles) so the DB is not doing redundant work and the UI shows the latest result.
 */
export const useFlow = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(null);

  const execute = useCallback(async (queryDSL, teamId = null) => {
    inFlightRef.current?.abort();
    const ac = new AbortController();
    inFlightRef.current = ac;

    setLoading(true);
    setError(null);

    try {
      const response = await analyticsApi.executeFlow(queryDSL, teamId, { signal: ac.signal });
      // The backend wraps results as { success: true, data: { nodes, links, anchors, totalUsers } }
      const engineResult = response?.data?.data ?? response?.data ?? null;
      setResult(engineResult);
      return engineResult;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      if (!analyticsApi.isAbortedOrCancelled(err)) {
        setError(errorMessage);
        setResult(null);
      }
      return null;
    } finally {
      if (inFlightRef.current === ac) {
        setLoading(false);
      }
    }
  }, []);

  const clearResult = useCallback(() => {
    inFlightRef.current?.abort();
    inFlightRef.current = null;
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  return { result, loading, error, execute, clearResult };
};
